import { Flex, MenuItem, Tag, Text } from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultIconSize, Icons } from '../Icons';
import { ErrorDetails } from '../alert/ErrorDetails';
import { trackerEvents } from '../tracker/events';
import { sampleEvents } from './events';

interface Props {
  sampleIds: number[];
  onStopFollowing: ((ids: number[]) => void) | null;
}

export function SampleTrackingMenuItems({ sampleIds, onStopFollowing }: Props) {
  const { t } = useTranslation();
  const idsKey = [...new Set(sampleIds)].sort((a, b) => a - b).join(',');
  const { data, refetch, loading, error } = useApiResource(samplesApi.tracking);
  const {
    request: follow,
    loading: following,
    error: followError,
  } = useApiRequest(samplesApi.follow);
  const reload = useCallback(() => {
    if (idsKey) void refetch(idsKey.split(',').map(Number));
  }, [idsKey, refetch]);
  const unfollowedIds =
    data?.filter((x) => !x.isFollowing).map((x) => x.sampleId) ?? [];
  const followedIds =
    data?.filter((x) => x.isFollowing).map((x) => x.sampleId) ?? [];

  useEffect(() => {
    reload();
    const stopUpdated = sampleEvents.onSamplesUpdated.sub(reload);
    const stopTracking = sampleEvents.onTrackingChanged.sub((event) => {
      if (event.sampleIds.some((id) => idsKey.split(',').includes(String(id))))
        reload();
    });
    return () => {
      stopUpdated();
      stopTracking();
    };
  }, [idsKey, reload]);

  const startFollowing = async () => {
    const response = await follow(unfollowedIds);
    if (!response.hasError) {
      sampleEvents.onTrackingChanged.dispatch({ sampleIds: unfollowedIds });
      trackerEvents.onAdded.dispatch();
    }
  };

  return (
    <>
      {error && <ErrorDetails error={error} />}
      {followError && <ErrorDetails error={followError} />}
      {(loading || !data) && !error && (
        <MenuItem isDisabled={true}>{t('general.loading')}</MenuItem>
      )}
      {unfollowedIds.length > 0 && (
        <MenuItem
          closeOnSelect={false}
          onClick={startFollowing}
          isDisabled={loading || following}
          icon={<Icons.Eye boxSize={defaultIconSize} />}
        >
          <Flex gap={1} alignItems="center">
            <Tag>{unfollowedIds.length}</Tag>
            <Text>{t('sample.follow')}</Text>
          </Flex>
        </MenuItem>
      )}
      {followedIds.length > 0 && onStopFollowing && (
        <MenuItem
          onClick={() => onStopFollowing(followedIds)}
          isDisabled={loading || following}
          icon={<Icons.TimelineRemove boxSize={defaultIconSize} />}
        >
          <Flex gap={1} alignItems="center">
            <Tag>{followedIds.length}</Tag>
            <Text>{t('sample.stopFollow')}</Text>
          </Flex>
        </MenuItem>
      )}
      {data &&
        data.length > 0 &&
        unfollowedIds.length === 0 &&
        (!onStopFollowing || followedIds.length === 0) && (
          <MenuItem
            isDisabled={true}
            icon={<Icons.Eye boxSize={defaultIconSize} />}
          >
            {t('sample.following')}
          </MenuItem>
        )}
    </>
  );
}
